import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import SplitMainLayout from './layouts/SplitMainLayout.js';
import useAuthData from './hooks/useAuthData';
import PaginaLogin from './paginas/PaginaLogin.js';
import PaginaInicio from './paginas/PaginaInicio.js';
import PaginaUsuarios from './paginas/PaginaUsuarios.js';
import PaginaDepartamentos from './paginas/PaginaDepartamentos.js';
import PaginaDashboards from './paginas/PaginaDashboards.js';
import PaginaGrupos from './paginas/PaginaGrupos.js';
import PaginaViewGrupo from './paginas/PaginaViewGrupo.js';
import PaginaRelatorio from './paginas/PaginaRelatorio.js';
import Erro404 from './paginas/Erro404.js';
import PaginaFavoritos from './paginas/PaginaFavoritos.js';
import PaginaLog from './paginas/PaginaLog.js';
import PaginaPermissoes from './paginas/PaginaPermissoes.js';

const rotasPublicas = ['/login', '/error'];

const salvarUrlAnterior = (rotaAtual) => {
  if (!rotasPublicas.includes(rotaAtual)) {
    sessionStorage.setItem('urlAnterior', rotaAtual);
  }
};

const WithMainLayout = ({ children, permission }) => {
  const { hasPermission, isLoading, error } = useAuthData();
  const token = localStorage.getItem('token');
  const localizacao = useLocation();

  // Salva a URL anterior
  salvarUrlAnterior(localizacao.pathname);

  // Verifica se não há token
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Aguarda carregamento
  if (isLoading) {
    return (
        <div className="flex items-center justify-center min-h-screen">
            <Spin size="large" />
        </div>
    );
  }

  // Trata erro de autenticação
  if (error) {
    return <Navigate to="/login" replace />;
  }

  // Verifica permissão
  if (!hasPermission(permission)) {
    return <Navigate to="/error" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

const WithSplitMainLayout = ({ children, permission }) => {
  const { hasPermission, isLoading, error } = useAuthData();
  const token = localStorage.getItem('token');
  const localizacao = useLocation();

  // Salva a URL anterior
  salvarUrlAnterior(localizacao.pathname);

  // Verifica se não há token
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Aguarda carregamento
  if (isLoading) {
    return <div>Carregando...</div>;
  }

  // Trata erro de autenticação
  if (error) {
    return <Navigate to="/login" replace />;
  }

  // Verifica permissão
  if (!hasPermission(permission)) {
    return <Navigate to="/error" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const cachedPaginaInicio = useMemo(() => <PaginaInicio />, []);
  const cachedPaginaFavoritos = useMemo(() => <PaginaFavoritos />, []);
  const cachedPaginaUsuarios = useMemo(() => <PaginaUsuarios />, []);
  const cachedPaginaDepartamentos = useMemo(() => <PaginaDepartamentos />, []);
  const cachedPaginaDashboards = useMemo(() => <PaginaDashboards />, []);
  const cachedPaginaGrupos = useMemo(() => <PaginaGrupos />, []);

  return (
    <Routes>
      {/* Rotas públicas */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<PaginaLogin />} />
      
      {/* Rotas protegidas para usuários */}
      <Route path="/inicio" element={
        <WithMainLayout permission="usuario">
          {cachedPaginaInicio}
        </WithMainLayout>
      } />
      {/*<Route path="/favoritos" element={*/}
      {/*  <WithMainLayout permission="usuario">*/}
      {/*    {cachedPaginaFavoritos}*/}
      {/*  </WithMainLayout>*/}
      {/*} />*/}
      
      {/* Rotas protegidas para gerenciadores */}
      <Route path="/usuarios" element={
        <WithMainLayout permission="gerenciador">
          {cachedPaginaUsuarios}
        </WithMainLayout>
      } />
      <Route path="/departamentos" element={
        <WithMainLayout permission="gerenciador">
          {cachedPaginaDepartamentos}
        </WithMainLayout>
      } />
      <Route path="/dashboards" element={
        <WithMainLayout permission="gerenciador">
          {cachedPaginaDashboards}
        </WithMainLayout>
      } />
      <Route path="/grupos" element={
        <WithMainLayout permission="gerenciador">
          {cachedPaginaGrupos}
        </WithMainLayout>
      } />
      
      {/* Rotas protegidas para administradores */}
      <Route path="/usuarios/logs" element={
        <WithMainLayout permission="administrador">
          <PaginaLog />
        </WithMainLayout>
      } />
      <Route path="/usuarios/permissoes" element={
        <WithMainLayout permission="administrador">
          <PaginaPermissoes />
        </WithMainLayout>
      } />
      
      {/* Rotas protegidas com parâmetros */}
      <Route path="/grupo/:id" element={
        <WithSplitMainLayout permission="gerenciador">
          <PaginaViewGrupo />
        </WithSplitMainLayout>
      } />
      <Route path="/relatorio/:id" element={
        <WithMainLayout permission="usuario">
          <PaginaRelatorio />
        </WithMainLayout>
      } />
      
      {/* Rotas de erro */}
      <Route path="/grupo" element={<Erro404 />} />
      <Route path="/relatorio" element={<Erro404 />} />
      <Route path="*" element={<Erro404 />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
